import React from 'react'
import { useCallback, useContext, useEffect, useMemo } from "react"
import _ from 'lodash'
import styled from '@emotion/styled'
import { cidForDirItemId, isConceptStatus } from "@fusion-advantage/formlib"
import { useFormDispatch } from "../../../redux/formStoreHooks"
import { useConcepts } from "../../../redux/selectConcept"
import { useConceptStatuses } from "../../../redux/selectConceptStatus2"
import { L3AmputationList, L3AmputationListModel } from "../l3-hardcode/L3AmputationList"
import { L1HumanoidMarker, MarkerProps } from "../l1/L1HumanDiagramMarker"
import { submitStatusUpdates } from "../../../redux/submitStatusUpdates"
import { ConceptStatusValueType } from "@fusion-advantage/backcompat"
import { FOOT_W, HAND_W, HUMAN_W, HumanDiagram, L1HumanFootRoot, L1HumanFootVector, L1HumanHandRoot, L1HumanHandVector, L1HumanoidFrontVector, L1HumanoidRoot, Side } from "../l1/L1HumanDiagram"
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome'
import { faSquareL, faSquareR } from "@fortawesome/pro-solid-svg-icons"
import L6FormContext from "../l6/L6FormContext"
import { FusionContentZIndex } from '../../../theme/FusionStyle'

interface SideMarkerProps {
	cx: number
	cy: number
	// 20260512 adding `icon` explicitly to fix new ts error
	// after upgrading packages
	icon: FontAwesomeIconProps["icon"]
}

// 20260512 typing to React.FC<SideMarkerProps> explicitly
// to fix new ts error after upgrading packages
export const SideMarker: React.FC<SideMarkerProps> = styled(FontAwesomeIcon)<SideMarkerProps>(({theme, cx, cy}) => ({
	fontSize: 24,
	position: 'absolute',
	zIndex: FusionContentZIndex.Content + 2,
	left: (cx - 10.5),
	top: cy,
	color: theme.colors.inputLabel,
}))

interface Amputation {
	dirItemId: number,
	cx: number,
	cy: number,
	rotateDeg?: number,
	diagram: HumanDiagram,
	side?: Side,
}

interface AmputationSideProps {
	side: Side
}

export const AMPUTATIONS: Amputation[] = [
	{ dirItemId: 968, cx: 48, cy: 130, diagram: HumanDiagram.Humanoid, },
	{ dirItemId: 841, cx: 36, cy: 188, diagram: HumanDiagram.Humanoid, },
	{ dirItemId: 830, cx: 80, cy: 296, diagram: HumanDiagram.Humanoid, },
	{ dirItemId: 832, cx: 82, cy: 368, diagram: HumanDiagram.Humanoid, },
	{ dirItemId: 838, cx: 80, cy: 432, diagram: HumanDiagram.Humanoid, },
	{ dirItemId: 889, cx: (HUMAN_W-48), cy: 130, diagram: HumanDiagram.Humanoid, },
	{ dirItemId: 842, cx: (HUMAN_W-36), cy: 188, diagram: HumanDiagram.Humanoid, },
	{ dirItemId: 831, cx: (HUMAN_W-80), cy: 296, diagram: HumanDiagram.Humanoid, },
	{ dirItemId: 833, cx: (HUMAN_W-82), cy: 368, diagram: HumanDiagram.Humanoid, },
	{ dirItemId: 839, cx: (HUMAN_W-80), cy: 432, diagram: HumanDiagram.Humanoid, },
	{ dirItemId: 1006, cx: 42, cy: 92, diagram: HumanDiagram.Hand, side: Side.Left },
	{ dirItemId: 844, cx: 82, cy: 128, rotateDeg: 30, diagram: HumanDiagram.Hand, side: Side.Left },
	{ dirItemId: 1005, cx: (HAND_W - 42), cy: 92, diagram: HumanDiagram.Hand, side: Side.Right },
	{ dirItemId: 843, cx: (HAND_W - 82), cy: 128, rotateDeg: -30, diagram: HumanDiagram.Hand, side: Side.Right },
	{ dirItemId: 835, cx: 88, cy: 62, diagram: HumanDiagram.Foot, side: Side.Left },
	{ dirItemId: 837, cx: 36, cy: 74, rotateDeg: -25, diagram: HumanDiagram.Foot, side: Side.Left },
	{ dirItemId: 1042, cx: 42, cy: 162, diagram: HumanDiagram.Foot, side: Side.Left },
	{ dirItemId: 834, cx: (FOOT_W - 88), cy: 62, diagram: HumanDiagram.Foot, side: Side.Right },
	{ dirItemId: 836, cx: (FOOT_W - 36), cy: 74, rotateDeg: 25, diagram: HumanDiagram.Foot, side: Side.Right },
	{ dirItemId: 1041, cx: (FOOT_W - 42), cy: 162, diagram: HumanDiagram.Foot, side: Side.Right },
]

interface L4AmputationListProps {
	xpath?: string
	onAddClick?: (e: React.MouseEvent<HTMLButtonElement>) => void,
}

export const L4AmputationList = ({xpath, onAddClick}: L4AmputationListProps) => {
	const cids = useMemo(() => {
		return AMPUTATIONS.map(m => cidForDirItemId(m.dirItemId))
	}, [AMPUTATIONS])
	const concepts = useConcepts(cids)
	const statuses = useConceptStatuses(cids)
	const dispatch = useFormDispatch()
	const models = useMemo(() => {
		const models: L3AmputationListModel[] = []
		_.forEach(concepts, (concept, cid) => {
			const status = statuses[cid]
			if(isConceptStatus(status) && status.value === true) {
				models.push({
					cid,
					dirItemId: concept.dirItemId ?? 0,
					title: concept.title ?? '',
				})
			}
		})
		return models
	}, [concepts, statuses])
	const shouldPrint = useCallback(() => {
		return models.length > 0
	}, [models])
	const { setXpathValuedDeterminer } = useContext(L6FormContext)
	useEffect(() => {
		if(xpath) {
			setXpathValuedDeterminer(xpath, shouldPrint)
			return () => {
				setXpathValuedDeterminer(xpath, false)
			}
		}
	}, [xpath, shouldPrint, setXpathValuedDeterminer ])
	const deleteModel = useCallback((model: L3AmputationListModel) => {
		const conceptValues = [{
			cid: model.cid,
			value: false,
			valueType: ConceptStatusValueType.Chosen,
		}]
		dispatch(submitStatusUpdates(conceptValues))
	}, [dispatch])
	return (
		<L3AmputationList
			models={models}
			deleteModel={deleteModel}
			onAddClick={onAddClick}
		/>
	)
}

const useL4AmputationMarkerProps = (amputations: Amputation[]) => {
	const cids = useMemo(() => {
		return amputations.map(m => cidForDirItemId(m.dirItemId))
	}, [amputations])
	const statuses = useConceptStatuses(cids)
	const dispatch = useFormDispatch()
	const updateModel = useCallback((cid: string, active: boolean) => {
		const conceptValues = [{
			cid,
			value: active,
			valueType: ConceptStatusValueType.Chosen,
		}]
		dispatch(submitStatusUpdates(conceptValues))
	}, [dispatch])
	const models = useMemo(() => {
		const models: MarkerProps[] = []
		_.forEach(amputations, amputation => {
			const cid = cidForDirItemId(amputation.dirItemId)
			const status = statuses[cid]
			const active = (isConceptStatus(status) && status.value === true)
			models.push({
				cx: amputation.cx,
				cy: amputation.cy,
				rotateDeg: amputation.rotateDeg,
				active,
				onClick: () => updateModel(cid, !active),
			})
		})
		return models
	}, [statuses, updateModel])
	return models
}

export const L4AmputationHumanoid = () => {
	const amputations = useMemo(() => {
		return AMPUTATIONS.filter(m => m.diagram == HumanDiagram.Humanoid)
	}, [AMPUTATIONS])
	const models = useL4AmputationMarkerProps(amputations)
	return (
		<L1HumanoidRoot>
			<L1HumanoidFrontVector />
			{models.map((marker, idx) => (
				<L1HumanoidMarker key={idx} {...marker} />
			))}
			<SideMarker icon={faSquareR} cx={32} cy={28} />
			<SideMarker icon={faSquareL} cx={HUMAN_W - 32} cy={28} />
		</L1HumanoidRoot>
	)
}

export const L4AmputationHand = ({side}: AmputationSideProps) => {
	const amputations = useMemo(() => {
		return AMPUTATIONS.filter(m => m.diagram == HumanDiagram.Hand && m.side == side)
	}, [AMPUTATIONS, side])
	const models = useL4AmputationMarkerProps(amputations)
	return (
		<L1HumanHandRoot>
			<L1HumanHandVector side={side} />
			{models.map((marker, idx) => (
				<L1HumanoidMarker key={idx} {...marker} />
			))}
		</L1HumanHandRoot>
	)
}

export const L4AmputationFoot = ({side}: AmputationSideProps) => {
	const amputations = useMemo(() => {
		return AMPUTATIONS.filter(m => m.diagram == HumanDiagram.Foot && m.side == side)
	}, [AMPUTATIONS, side])
	const models = useL4AmputationMarkerProps(amputations)
	return (
		<L1HumanFootRoot>
			<L1HumanFootVector side={side} />
			{models.map((marker, idx) => (
				<L1HumanoidMarker key={idx} {...marker} />
			))}
		</L1HumanFootRoot>
	)
}

const L4AmputationDiagramsRoot = styled.div(({theme}) => ({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	justifyContent: 'center',
	columnGap: theme.spacing(6),
	marginBottom: theme.spacing(2),

	// color of humanoid strokes
	color: theme.colors.inputLabel,
}))

const AppendageGrid = styled.div(({theme}) => ({
	display: 'grid',
	gridTemplateColumns: '1fr 1fr',
	gap: theme.spacing(4),
	position: 'relative',
}))

export const L4AmputationDiagrams = () => {
	return (
		<L4AmputationDiagramsRoot>
			<L4AmputationHumanoid />
			<AppendageGrid>
				<L4AmputationHand side={Side.Left} />
				<L4AmputationHand side={Side.Right} />
				<L4AmputationFoot side={Side.Left} />
				<L4AmputationFoot side={Side.Right} />
				<SideMarker icon={faSquareL} cx={94} cy={196} />
				<SideMarker icon={faSquareR} cx={144} cy={196} />
			</AppendageGrid>
		</L4AmputationDiagramsRoot>
	)
}
